import AddRobotModal from '../AddRobotModal/AddRobotModal'
import { RobotApi } from 'api/RobotApi'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import FavoriteStar from 'components/FavoriteStar/FavoriteStar'
import ServiceReportEmbed from 'components/ServiceReportEmbed/ServiceReportEmbed'
import { useUser } from 'hooks/user'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import Tooltip from '@mui/material/Tooltip'
import { useMemo, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH, ROBOT_NETWORK_STATUSES } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { IRobotInfo, IStation } from 'utils/interfaces'
import { buildServiceReportParameters, canSeeServiceReport } from 'utils/serviceReportForm'

import RobotModeIcons from './RobotModeIcons'
import RobotStatusIcons from './RobotStatusIcons'

import style from './RobotItem.module.css'

const RobotItem = ({
  robot,
  onRobotUpdated,
  stations,
  robots,
  hightlighted,
  onStationsChanged,
  hideDelete,
  onExclude,
}: {
  robot: IRobotInfo
  stations: IStation[]
  robots: IRobotInfo[]
  onRobotUpdated: () => void
  hightlighted: boolean
  onStationsChanged: () => void
  hideDelete?: boolean
  onExclude?: (robot: IRobotInfo) => Promise<void> | void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useUser()
  const showServiceReport = canSeeServiceReport({
    audience: user?.state.audience ?? null,
    vendor: robot.vendor,
  })
  const goToRobot = (robotId: number) => {
    navigate(`${PATH.ROBOT}${robotId}`)
  }
  const [deleteRobotDialog, setDeleteRobotDialog] = useState(false)
  const [editingRobot, setEditingRobot] = useState(false)
  const [serviceReportOpen, setServiceReportOpen] = useState(false)
  const [serialCopied, setSerialCopied] = useState(false)

  const serviceReportParameters = useMemo(
    () =>
      buildServiceReportParameters({
        serialNumber: robot.serialNumber,
        robotType: robot.robot_type,
        robotTypeDisplay: robot.robot_type_display,
        vendor: robot.vendor,
        firstName: user?.state.firstName,
        lastName: user?.state.lastName,
      }),
    [
      robot.serialNumber,
      robot.robot_type,
      robot.robot_type_display,
      robot.vendor,
      user?.state.firstName,
      user?.state.lastName,
    ]
  )

  const deleteRobot = async () => {
    try {
      await RobotApi.deleteRobot(robot.id)
      setDeleteRobotDialog(false)
      onRobotUpdated()
    } catch (error) {
      console.log('Error deleting robot:', error)
    }
  }

  const copySerialNumber = async (event: MouseEvent) => {
    event.stopPropagation()
    const serial = String(robot.serialNumber || '').trim()
    if (!serial) return
    try {
      await navigator.clipboard.writeText(serial)
      setSerialCopied(true)
      window.setTimeout(() => setSerialCopied(false), 1500)
    } catch (error) {
      console.log('Error copying robot serial number:', error)
    }
  }

  const diagLevel = robot.systemDiagnostics?.level ?? 0
  const isError = robot.emergencyButton || diagLevel >= 2
  const isWarning = diagLevel === 1
  const isOffline = robot.networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE

  return (
    <>
      <div
        className={clsx(style.tableRow, {
          [style.highlighted]: hightlighted,
          [style.errorRow]: isError,
          [style.warningRow]: isWarning && !isError,
          [style.offlineRow]: isOffline,
        })}
        onClick={() => goToRobot(robot.id)}
        key={robot.id}
      >
        <FavoriteStar kind="robot" id={robot.id} />
        <Tooltip
          title={serialCopied ? t('robots.serial_number_copied') : t('robots.copy_serial_number')}
          open={serialCopied ? true : undefined}
          placement="top"
          enterDelay={300}
        >
          <span
            className={clsx(style.tableCol, style.serialCol, { [style.serialColCopied]: serialCopied })}
            onClick={copySerialNumber}
            data-test-id={getTestId('copy-robot-serial')}
          >
            {robot.serialNumber}
          </span>
        </Tooltip>
        <span className={style.tableCol}>{robot.name}</span>
        {/* <span className={style.tableCol}>{robot.activeTask?.missionName}</span> */}
        <span className={style.statusCol}>
          <RobotStatusIcons robot={robot} />
        </span>
        <span className={style.modeCol}>
          <RobotModeIcons robot={robot} />
        </span>
        <span className={style.colCenter}>
          <ActionsMenu>
            <ActionsMenuItem
              onClick={() => {
                navigate(`${PATH.ANALYTICS}?robot_sn=${robot.serialNumber}`)
              }}
            >
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Analytics</span>
            </ActionsMenuItem>
            {showServiceReport && (
              <ActionsMenuItem onClick={() => setServiceReportOpen(true)}>
                <span className={style.menuItemIcon} />
                <span className={style.menuItemText}>{t('robot.service_report')}</span>
              </ActionsMenuItem>
            )}
            <ActionsMenuItem onClick={() => setEditingRobot(true)} data-test-id={getTestId('edit-zone-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                {t('common.edit')}
              </span>
            </ActionsMenuItem>
            {/* {onExclude && (
              <ActionsMenuItem onClick={() => onExclude(robot)} data-test-id={getTestId('exclude-robot-from-facility')}>
                <span className={style.menuItemIcon} />
                <span className={style.menuItemText}>Exclude</span>
              </ActionsMenuItem>
            )} */}
            {!hideDelete && (
              <ActionsMenuItem onClick={() => setDeleteRobotDialog(true)} data-test-id={getTestId('remove-zone-list')}>
                <RemoveSvg className={style.menuItemIcon} />
                <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
              </ActionsMenuItem>
            )}
          </ActionsMenu>
        </span>
      </div>
      <ConfirmDialog
        title={t('robots.delete_question')}
        isOpen={deleteRobotDialog}
        onClose={() => setDeleteRobotDialog(false)}
        onConfirmClick={deleteRobot}
        confirmText={t('common.delete')}
        danger={true}
      />
      <AddRobotModal
        isOpen={editingRobot}
        robots={robots}
        onClose={() => setEditingRobot(false)}
        onSave={() => {
          onRobotUpdated()
          setEditingRobot(false)
        }}
        robotExisting={robot}
      />
      {showServiceReport && (
        <ServiceReportEmbed
          isOpen={serviceReportOpen}
          onClose={() => setServiceReportOpen(false)}
          parameters={serviceReportParameters}
        />
      )}
    </>
  )
}

export default RobotItem
